import type { Job } from "../api.js";
import { duration } from "./ui.js";

const NODE_W = 210;
const NODE_H = 76;
const GAP_X = 64;
const GAP_Y = 14;
const PAD = 8;

type Tone = "ok" | "err" | "run" | "muted";

const TONE: Record<string, Tone> = {
  succeeded: "ok",
  failed: "err",
  running: "run",
  assigned: "run",
};

const tone = (status: string): Tone => TONE[status] ?? "muted";

export interface Placed {
  job: Job;
  depth: number;
  row: number;
  x: number;
  y: number;
}

/** Columns by longest dependency chain, rows in job order. Unknown `needs` are ignored. */
export function layoutJobs(jobs: readonly Job[]): Placed[] {
  const byKey = new Map(jobs.map((j) => [j.key, j]));
  const depths = new Map<string, number>();
  const depthOf = (job: Job, seen: readonly string[] = []): number => {
    const known = depths.get(job.key);
    if (known !== undefined) return known;
    if (seen.includes(job.key)) return 0;
    const parents = job.needs.flatMap((k) => byKey.get(k) ?? []);
    const depth = parents.length === 0 ? 0 : 1 + Math.max(...parents.map((p) => depthOf(p, [...seen, job.key])));
    depths.set(job.key, depth);
    return depth;
  };
  const rows = new Map<number, number>();
  return jobs.map((job) => {
    const depth = depthOf(job);
    const row = rows.get(depth) ?? 0;
    rows.set(depth, row + 1);
    return { job, depth, row, x: PAD + depth * (NODE_W + GAP_X), y: PAD + row * (NODE_H + GAP_Y) };
  });
}

function StepStrip({ job }: { job: Job }) {
  const firstPending = job.status === "running" ? job.steps.findIndex((s) => s.status === "pending") : -1;
  return (
    <span className="graph-steps" aria-hidden>
      {job.steps.map((s, i) => (
        <span key={s.name} className={`graph-step graph-step-${i === firstPending ? "running" : s.status}`} title={`${s.name}: ${i === firstPending ? "running" : s.status}`} />
      ))}
    </span>
  );
}

/** Jobs as boxes by dependency depth, joined by arrows; click a box to select it. */
export function PipelineGraph({ jobs, selectedId, onSelect }: { jobs: readonly Job[]; selectedId: string | undefined; onSelect: (id: string) => void }) {
  const placed = layoutJobs(jobs);
  const at = new Map(placed.map((p) => [p.job.key, p]));
  const width = PAD * 2 + (Math.max(0, ...placed.map((p) => p.depth)) + 1) * NODE_W + Math.max(0, ...placed.map((p) => p.depth)) * GAP_X;
  const height = PAD * 2 + Math.max(1, ...placed.map((p) => p.row + 1)) * (NODE_H + GAP_Y) - GAP_Y;
  return (
    <div className="graph-scroll">
      <div className="graph" style={{ width, height }} role="list" aria-label="Pipeline jobs">
        <svg className="graph-edges" width={width} height={height} aria-hidden>
          {placed.flatMap((to) =>
            to.job.needs.flatMap((key) => {
              const from = at.get(key);
              if (!from) return [];
              const x1 = from.x + NODE_W;
              const y1 = from.y + NODE_H / 2;
              const x2 = to.x;
              const y2 = to.y + NODE_H / 2;
              const mid = (x1 + x2) / 2;
              return (
                <path key={`${key}->${to.job.key}`} className={`graph-edge graph-edge-${tone(from.job.status)}`} d={`M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2 - 4},${y2}`} markerEnd="url(#arrow)" />
              );
            }),
          )}
          <defs>
            <marker id="arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" className="graph-arrow" />
            </marker>
          </defs>
        </svg>
        {placed.map(({ job, x, y }) => (
          <button
            key={job.id}
            role="listitem"
            className={`graph-node graph-node-${tone(job.status)} ${job.status === "skipped" ? "graph-node-skipped" : ""} ${selectedId === job.id ? "selected" : ""}`}
            style={{ left: x, top: y, width: NODE_W, height: NODE_H }}
            onClick={() => {
              onSelect(job.id);
            }}
            title={job.name}
          >
            <span className="graph-node-head">
              <span className={`graph-dot graph-dot-${tone(job.status)}`} aria-hidden />
              <span className="graph-status">{job.status.replace(/-/g, " ")}</span>
              <span className="muted small graph-time">{duration(job.startedAt, job.finishedAt)}</span>
            </span>
            <span className="graph-name">{job.name}</span>
            <StepStrip job={job} />
          </button>
        ))}
      </div>
    </div>
  );
}
